// PanelSelector.tsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, App } from 'antd';
import { useTranslation } from "react-i18next";
import { useUserPanel } from "../../contexts/UserPanelContext";
import { useTheme } from "../../contexts/ThemeContext";
import { getAndSanitize } from '../utils/sanitization';

interface Panel {
  id: string;
  name?: string;
  isActive?: boolean;
  adminId?: string; // Added to support filtering
  ownerId?: string; // Added to support filtering
  createdBy?: string; // Added to support filtering
}

interface PanelSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onPanelSelect: (panelId: string) => void;
  panels: Panel[];
  loading: boolean;
  actionTitle: string;
  actionDescription?: string;
  zIndex?: number;
}

const PanelSelector: React.FC<PanelSelectorProps> = ({
  isOpen,
  onClose,
  onPanelSelect,
  panels,
  loading,
  actionTitle,
  actionDescription,
  zIndex = 1001,
}) => {
  const [selectedPanelId, setSelectedPanelId] = useState<string | undefined>(undefined);
  const [filteredPanels, setFilteredPanels] = useState<Panel[]>([]);
  const { currentPanelId } = useUserPanel();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { message: contextMessage } = App.useApp();
  
  // Filter panels to only those owned by current admin
  useEffect(() => {
    if (!panels || !Array.isArray(panels)) {
      setFilteredPanels([]);
      return;
    }
    
    const adminId = getAndSanitize("userId");
    if (!adminId) {
      setFilteredPanels(panels); // Fallback to all panels if no adminId
      return;
    }
    
    
    // Filter panels to only those matching adminId
    const adminPanels = panels.filter(panel => 
      (panel.adminId === adminId) || 
      (panel.ownerId === adminId) || 
      (panel.createdBy === adminId)
    );
    
    if (adminPanels.length === 0) {
      console.warn("[PanelSelector] Filtering removed all panels, using original list");
      setFilteredPanels(panels); // Fallback to all panels if filtering removed everything
    } else {
      setFilteredPanels(adminPanels);
    }
  }, [panels]);

  // Handle initial selection when modal opens
  useEffect(() => {
    if (isOpen) {
      if (currentPanelId && filteredPanels.some(p => p.id === currentPanelId)) {
        setSelectedPanelId(currentPanelId);
      } else if (filteredPanels.length > 0) {
        setSelectedPanelId(filteredPanels[0].id);
      } else {
        setSelectedPanelId(undefined);
      }
    }
  }, [isOpen, currentPanelId, filteredPanels]);

  const handleSelect = () => {
    if (!selectedPanelId) {
      contextMessage.warning(t('panelSelector.selectPanelFirst', 'Por favor, selecciona un panel primero'));
      return;
    }
    
    onPanelSelect(selectedPanelId);
    onClose();
  };

  return (
    <Modal
      title={t('panelSelector.title', 'Seleccionar Panel')}
      open={isOpen}
      onCancel={onClose}
      zIndex={zIndex}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('common.cancel', 'Cancelar')}
        </Button>,
        <Button 
          key="select" 
          type="primary" 
          onClick={handleSelect}
          disabled={!selectedPanelId || loading}
        >
          {t('common.select', 'Seleccionar')}
        </Button>
      ]}
    >
      <div style={{ marginBottom: 20 }}>
        <h4>{actionTitle}</h4>
        {actionDescription && (
          <p style={{ 
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' 
          }}>
            {actionDescription}
          </p>
        )}
      </div>
      
      <div style={{ marginBottom: 20 }}>
        <div style={{ 
          fontWeight: 'bold', 
          marginBottom: 8,
          color: theme === 'dark' ? '#ffffff' : '#000000'
        }}>
          {t('panelSelector.selectPanel', 'Seleccionar Panel')}:
        </div>
        
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '10px', 
          marginTop: '10px' 
        }}>
          {filteredPanels.map(panel => (
            <div 
              key={panel.id}
              onClick={() => setSelectedPanelId(panel.id)}
              style={{ 
                backgroundColor: theme === 'dark' ? '#1f1f1f' : '#f0f0f0',
                borderRadius: '8px',
                padding: '12px',
                minWidth: '120px',
                textAlign: 'center',
                cursor: 'pointer',
                boxShadow: theme === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                border: `2px solid ${selectedPanelId === panel.id 
                  ? '#ac4949' 
                  : theme === 'dark' ? '#333' : '#ddd'}`,
                transform: selectedPanelId === panel.id ? 'scale(1.05)' : 'scale(1)',
              }}
              className="panel-square"
            >
              <div style={{ 
                fontWeight: 'bold', 
                marginBottom: '4px',
                color: selectedPanelId === panel.id 
                  ? '#ac4949' 
                  : theme === 'dark' ? '#ffffff' : '#000000'
              }}>
                {panel.name || `Panel #${panel.id.substring(0, 6)}`}
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' 
              }}>
                {panel.id.substring(0, 8)}...
                {panel.isActive && (
                  <span style={{ 
                    marginLeft: '5px', 
                    color: '#52c41a',
                    fontSize: '11px' 
                  }}>
                    {t('panelSelector.active', 'Activo')}
                  </span>
                )}
                {panel.id === currentPanelId && (
                  <span style={{ 
                    marginLeft: '5px', 
                    color: '#1890ff',
                    fontSize: '11px' 
                  }}>
                    {t('panelSelector.current', 'Actual')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {filteredPanels.length === 0 && !loading && (
        <div style={{ color: '#faad14' }}>{t('panelSelector.noPanelsAvailable', 'No hay paneles disponibles')}</div>
      )}
    </Modal>
  );
};

export default PanelSelector;